import { create } from 'zustand'

export type MessageRole = 'user' | 'assistant' | 'system'
export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'error'

export interface ChatMessage {
  id: string
  role: MessageRole
  content: string
  timestamp: Date
  status: MessageStatus
  conversationId?: string
  metadata?: {
    type?: string
    embed_url?: string
    video_id?: string
    thumbnail?: string
    [key: string]: unknown
  }
}

export interface Conversation {
  id: string
  title: string
  lastMessage?: string
  lastMessageAt: Date
  messageCount: number
}

interface ChatState {
  messages: ChatMessage[]
  conversations: Conversation[]
  currentConversationId: string | null
  isTyping: boolean
  error: string | null

  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void
  setMessages: (messages: ChatMessage[]) => void
  setConversations: (conversations: Conversation[]) => void
  setCurrentConversation: (id: string | null, clearMessages?: boolean) => void
  setTyping: (isTyping: boolean) => void
  setError: (error: string | null) => void
  clearMessages: () => void
}

export const useChatStore = create<ChatState>((set) => ({
  messages: [],
  conversations: [],
  currentConversationId: null,
  isTyping: false,
  error: null,

  addMessage: (message) => {
    const id = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
    const newMessage: ChatMessage = {
      ...message,
      id,
      timestamp: new Date(),
    }

    // Check for duplicate - same role + content within last 5 seconds
    const isDuplicate = (state: ChatState) => {
      const recent = state.messages.slice(-5) // Check last 5 messages
      return recent.some(
        (m) =>
          m.role === message.role &&
          m.content === message.content &&
          Date.now() - m.timestamp.getTime() < 5000
      )
    }

    set((state) => {
      // Skip if duplicate
      if (isDuplicate(state)) {
        console.warn('[ChatStore] Skipping duplicate message')
        return state
      }
      return {
        messages: [...state.messages, newMessage],
      }
    })

    return id
  },

  updateMessage: (id, updates) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg
      ),
    }))
  },

  setMessages: (messages) => {
    set({ messages })
  },

  setConversations: (conversations) => {
    set({ conversations })
  },

  setCurrentConversation: (id, clearMessages = true) => {
    // Only clear messages when explicitly switching conversations, not when
    // establishing a new conversation ID for the current chat
    if (clearMessages) {
      set({ currentConversationId: id, messages: [] })
    } else {
      set({ currentConversationId: id })
    }
  },

  setTyping: (isTyping) => {
    set({ isTyping })
  },

  setError: (error) => {
    set({ error })
  },

  clearMessages: () => {
    set({ messages: [], currentConversationId: null })
  },
}))
